import { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { parseLocalDateTime } from 'utils/dates';
import { EvaluationTile } from './evaluation-tile';
import { EvaluationTileSkeleton } from './evaluation-tile-skeleton';
import type { EvaluationWorkspace, EvaluationCalculation } from '../types';
import style from './workspace-accordion.module.scss';

interface WorkspaceAccordionProps {
  workspace: EvaluationWorkspace;
  calculations: EvaluationCalculation[];
  onExpand?: () => void;
  selectedCalculationId: number | null;
  onCalculationSelect: (id: number) => void;
  isLoadingCalculations?: boolean;
}

/**
 * Accordion section for a single workspace in the left panel.
 * Manages its own expanded/collapsed state internally.
 * Displays workspace metadata in the summary and evaluation tiles in the body.
 *
 * @param {WorkspaceAccordionProps} props - Component props.
 * @return {JSX.Element} The workspace accordion.
 */
export const WorkspaceAccordion = ({
  workspace,
  calculations,
  onExpand,
  selectedCalculationId,
  onCalculationSelect,
  isLoadingCalculations,
}: WorkspaceAccordionProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-expand when this workspace contains the selected calculation.
  // Only expands — never collapses — so manual toggle isn't overridden.
  useEffect(() => {
    if (
      selectedCalculationId !== null &&
      calculations.some((c) => c.id === selectedCalculationId)
    ) {
      setIsExpanded(true);
    }
  }, [selectedCalculationId, calculations]);

  /**
   * Handles accordion state change. Notifies the parent via onExpand when opening.
   *
   * @param {boolean} next - The new expanded state.
   */
  const handleChange = (next: boolean) => {
    setIsExpanded(next);
    if (next && onExpand) {
      onExpand();
    }
  };

  return (
    <div className={style.workspace}>
      <Accordion expanded={isExpanded} onClick={handleChange}>
        <AccordionSummary>
          <div className={style.summaryContent}>
            <span className={style.workspaceName}>{workspace.name}</span>
            {/* `createdBy` is resolved from the PaperTrail create version, which
                a workspace can lack entirely — it is written `on: [:create]`, so
                any row created before the callback existed reports none. Reading
                `.name` off that null threw past the accordion and took the whole
                Eval 3 page down with it. */}
            <span className={style.meta}>
              Created by: {workspace.createdBy?.name ?? 'Unknown'}
            </span>
            <span className={style.meta}>
              Date Created: {parseLocalDateTime(workspace.createdAt, true)}
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {isLoadingCalculations ? (
            <div className={style.tiles}>
              <EvaluationTileSkeleton />
              <EvaluationTileSkeleton />
            </div>
          ) : calculations.length > 0 ? (
            <div className={style.tiles}>
              {calculations.map((calc) => (
                <EvaluationTile
                  key={calc.id}
                  calculation={calc}
                  isSelected={calc.id === selectedCalculationId}
                  onSelect={() => onCalculationSelect(calc.id)}
                />
              ))}
            </div>
          ) : (
            <div className={style.empty}>No evaluations in this workspace.</div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
